import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@devhop/ui';
import { HelpCircle, Plus } from 'lucide-react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import {
    usePromotionEmployeesLookupQuery,
    usePromotionPositionsLookupQuery,
    usePromotionRecordsQuery,
} from '../api/queries';
import {
    PromotionRecordsFilter,
    type PromotionRecordsSearchParams,
} from '../ui/PromotionRecordsFilter';
import { PromotionRecordsTable } from '../ui/PromotionRecordsTable';
import type { PromotionRecordListItem } from '@/modules/hr/promotion/domain/types';
import {
    PromotionTour,
    promotionRecordsTourSteps,
    usePromotionTour,
} from '../tour';

export function PromotionRecordsPage() {
    const nav = useNavigate({ from: '/app/hr/promotion/records' });
    const search = useSearch({
        from: '/app/hr/promotion/records',
    }) as PromotionRecordsSearchParams;

    const sortBy = search.sort?.[0]?.field ?? 'promotedAt';
    const sortOrder = (search.sort?.[0]?.dir ?? 'desc') as 'asc' | 'desc';

    const query = useMemo(
        () => ({
            offset: search.offset ?? 0,
            limit: search.limit ?? 20,
            sort: search.sort,
            employeeId: search.employeeId,
            periodYear: search.periodYear,
            periodMonth: search.periodMonth,
            positionId: search.positionId,
            search: search.search,
        }),
        [search],
    );

    const recordsQuery = usePromotionRecordsQuery(query);
    const employeesQuery = usePromotionEmployeesLookupQuery();
    const positionsQuery = usePromotionPositionsLookupQuery();

    const rows = recordsQuery.data?.data ?? [];

    const handlePage = (offset: number, limit: number) => {
        nav({
            search: {
                offset,
                limit,
                sort: search.sort,
                employeeId: search.employeeId,
                periodYear: search.periodYear,
                periodMonth: search.periodMonth,
                positionId: search.positionId,
                search: search.search,
            },
        });
    };

    const handleSort = useCallback(
        (id: string, desc: boolean) => {
            const nextSort = [{ field: id, dir: (desc ? 'desc' : 'asc') as 'asc' | 'desc' }];
            nav({
                search: {
                    offset: 0,
                    limit: search.limit ?? 20,
                    sort: nextSort,
                    employeeId: search.employeeId,
                    periodYear: search.periodYear,
                    periodMonth: search.periodMonth,
                    positionId: search.positionId,
                    search: search.search,
                },
            });
        },
        [nav, search],
    );

    const tour = usePromotionTour('records');

    return (
        <>
            <Header />
            <Main>
                <PromotionTour
                    steps={promotionRecordsTourSteps}
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-start justify-between gap-4"
                    data-tourid="promotion-records-toolbar"
                >
                    <div className="flex items-center gap-2">
                        <h1 className="font-semibold text-2xl">
                            ບັນທຶກການເລື່ອນຕຳແໜ່ງ
                        </h1>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 rounded-full"
                            onClick={tour.startTour}
                            title="ເລີ່ມທົດສອບການນຳທາງ"
                            aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                        >
                            <HelpCircle className="size-5" />
                        </Button>
                    </div>
                    <Button
                        onClick={() =>
                            nav({ to: '/app/hr/promotion/records/new' })
                        }
                        className="gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        ສ້າງບັນທຶກການເລື່ອນຕຳແໜ່ງ
                    </Button>
                </div>

                <div
                    className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
                    data-tourid="promotion-records-filter"
                >
                    <PromotionRecordsFilter
                        fromRoute="/app/hr/promotion/records"
                        searchParams={search}
                        employeeOptions={(employeesQuery.data ?? []).map(
                            (item) => ({ value: item.id, label: item.label }),
                        )}
                        positionOptions={(positionsQuery.data ?? []).map(
                            (item) => ({ value: item.id, label: item.label }),
                        )}
                    />
                    <div data-tourid="promotion-records-table">
                        <PromotionRecordsTable
                        data={rows}
                        isLoading={recordsQuery.isLoading}
                    offset={search.offset ?? 0}
                    limit={search.limit ?? 20}
                    totalCount={recordsQuery.data?.meta.total ?? 0}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortingChange={handleSort}
                    onPaginationChange={handlePage}
                    onView={(row) =>
                        nav({
                            to: '/app/hr/promotion/records/$id',
                            params: { id: row.id },
                        })
                    }
                    onEdit={(row) =>
                        nav({
                            to: '/app/hr/promotion/records/$id/edit',
                            params: { id: row.id },
                        })
                    }
                    />
                    </div>
                </div>
            </Main>
        </>
    );
}
